import React, { useState, useContext, useEffect } from 'react'
import cn from 'classnames'
import moment from 'moment'
import { makeStyles } from '@material-ui/core'
import papaParse from 'papaparse'
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Link,
} from '@material-ui/core'
import FileCopyTwoToneIcon from '@material-ui/icons/FileCopyTwoTone'
import ExpandCollapseBox from 'Components/ExpandCollapseBox'
import { ButtonInline } from 'Components/Buttons/ButtonInline'
import { Modal } from 'Components/Modal'
import { ModalHeader } from 'Components/Modal/ModalHeader'
import { ModalTitle } from 'Components/Modal/ModalTitle'
import { ModalBody } from 'Components/Modal/ModalBody'
import { ModalFooter } from 'Components/Modal/ModalFooter'
import SubmitButton from 'Components/Form/SubmitButton'
import { SnackBarContext } from 'Components/SnackBar'
import EditIcon from 'Assets/Images/icons/edit'
// import DownloadIcon from 'Assets/Images/icons/download'
import UploadIcon from 'Assets/Images/icons/upload'
import CancelIcon from 'Assets/Images/icons/cancel'
import { downloadStringAsFile } from 'Utils/Misc/index'
import { updateADMPrintingCosts } from '../../../../gql'
import {
  ADMDocumentFormats,
  ADMDocumentLabels,
  AggregationCostLabels,
} from '../../../../../../shared/constants'
import { apiClient } from '../../../../../../Module/API/Client.js'
import { authorization } from '../../../../../../Module/Auth/Auth.js'

import styles from './styles.js'

const useStyles = makeStyles(() => styles)

function formatTotalDataCost(value) {
  const num = Number(value)
  if (num === Math.floor(num)) {
    return num.toLocaleString()
  } else {
    return parseFloat(num.toFixed(4)).toLocaleString()
  }
}
export const ADMPrintingCosts = ({
  countryId,
  currencySign,
  ADMPrintingCostsData,
  isEditingTable,
  visible,
}) => {
  const user = authorization.getUser()
  const classes = useStyles()
  const [headers, setHeaders] = useState([])
  const [printingCosts, setPrintingCosts] = useState([])
  const [showModal, setShowModal] = useState(false)
  const [uploadedFile, setUploadedFile] = useState(null)
  const [fileValidationError, setFileValidationError] = useState('')
  const [lastUpdaterUser, setLastUpdaterUser] = useState()

  const runSnackBar = useContext(SnackBarContext)

  const groupByQuantity = array =>
    array.reduce((objectsByKeyValue, obj) => {
      const value = obj.flyersCount
      objectsByKeyValue[value] = (objectsByKeyValue[value] || []).concat(obj)
      return objectsByKeyValue
    }, {})

  const preparePrintingCostsData = nodes => {
    if (!nodes) return
    const sorterData = nodes.sort((a, b) => a.index - b.index)

    const headerStructure = {
      quantity: { label: 'Quantity', colspan: 1, parent: null },
      defaultPricing: {
        label: 'Default Pricing Table',
        colspan: 4,
        parent: null,
        children: [
          ADMDocumentFormats.POSTCARD_6x4,
          ADMDocumentFormats.POSTCARD_9x6,
          ADMDocumentFormats.POSTCARD_11x6,
          ADMDocumentFormats.POSTCARD_15x12,
        ],
      },
      aggregationCost: {
        label: 'Aggregation Cost (Data Axle)',
        colspan: 2,
        parent: null,
        children: ['DATA_COST', 'TOTAL_DATA_COST'],
      },
    }

    // Create flat headers array for data mapping
    const headers = [
      'FLYERS_COUNT',
      ...headerStructure.defaultPricing.children,
      ...headerStructure.aggregationCost.children,
    ]

    const costsGroupedByQuantity = groupByQuantity(sorterData)
    const mappedCosts = Object.entries(costsGroupedByQuantity).reduce(
      (acc, [key, costs]) => {
        const costData = costs.reduce(
          (accumulator, cost) => ({
            ...accumulator,
            [cost.label]: cost.value,
          }),
          {},
        )

        const dataCost = costData['DATA_COST'] || 0
        const quantity = Number(key)
        const totalDataCost = quantity * dataCost

        return [
          ...acc,
          {
            FLYERS_COUNT: key,
            ...costData,
            TOTAL_DATA_COST: totalDataCost,
          },
        ]
      },
      [],
    )
    setHeaders({ structure: headerStructure, flat: headers })
    setPrintingCosts(mappedCosts)
    setUploadedFile(null)
  }

  const savePrintingCosts = async data => {
    const response = await updateADMPrintingCosts({
      NewADMPrintingCosts: data,
    })
    const {
      data: {
        updateADMPrintingCosts: { ADMPrintingCosts, error },
      },
    } = await response.json()

    if (ADMPrintingCosts) {
      preparePrintingCostsData(ADMPrintingCosts)
      runSnackBar({
        type: 'success',
        msg: `Printing Costs were saved successfully`,
      })
    } else {
      setUploadedFile(null)
      runSnackBar({
        type: 'error',
        msg: `There is an error while saving printing cost.`,
        error: error,
      })
    }
  }

  // const unparseFile = async printingCosts => {
  //   const papaParse = await import(
  //     /* webpackChunkName: "papaparse" */ 'papaparse'
  //   )
  //   const csv = papaParse.unparse(printingCosts)

  //   const blob = new Blob([csv], { type: 'text/csv' })
  //   const url = window.URL.createObjectURL(blob)
  //   const a = document.createElement('a')
  //   a.setAttribute('href', url)
  //   a.setAttribute('download', 'download.csv')
  //   a.click()
  // }

  const parseFile = async () => {
    setShowModal(false)
    const papaParse = await import(
      /* webpackChunkName: "papaparse" */ 'papaparse'
    )
    papaParse.parse(uploadedFile, {
      complete: results => {
        const nonEmptyColumns = results.data[0].filter(header => header)
        const dataForSaving = nonEmptyColumns
          .slice(1)
          .filter(column => column !== 'TOTAL_DATA_COST') // Exclude calculated field
          .reduce((acc, column, index) => {
            const filteredPrintingCostsData = results.data
              .slice(1)
              .filter(data => data[0])
            return [
              ...acc,
              ...filteredPrintingCostsData.reduce(
                (accumulator, printingCostsData) => {
                  if (!printingCostsData.length || !printingCostsData[0])
                    return acc

                  const value = printingCostsData[index + 1]
                  if (!value) {
                    return accumulator
                  }

                  return [
                    ...accumulator,
                    {
                      countryId: Number(countryId),
                      label: column,
                      value: Number(Number(value).toFixed(4)),
                      flyersCount: Number(printingCostsData[0]),
                      updatedBy: Number(user.id),
                      version: ADMPrintingCostsData[0]
                        ? ADMPrintingCostsData[0].version + 1
                        : 1,
                    },
                  ]
                },
                [],
              ),
            ]
          }, [])
        savePrintingCosts(dataForSaving)
      },
    })
  }

  const getLastUpdaterUser = async id => {
    const req = await apiClient.getUserById(id)
    const updater = await req.json()
    if (updater) {
      setLastUpdaterUser(updater)
    }
  }

  useEffect(() => {
    if (
      !lastUpdaterUser &&
      ADMPrintingCostsData &&
      ADMPrintingCostsData.length
    ) {
      getLastUpdaterUser(ADMPrintingCostsData[0].updatedBy)
    }
  }, [ADMPrintingCostsData])

  useEffect(() => {
    preparePrintingCostsData(ADMPrintingCostsData)
  }, [])

  const validateUploadedFile = async file => {
    if (file.type !== 'text/csv') {
      return setFileValidationError('Wrong file type. CSV required.')
    }

    papaParse.parse(file, {
      complete: result => {
        const { data } = result
        while (data.length) {
          const lastRow = data.pop()
          if (lastRow.findIndex(item => item) !== -1) {
            data.push(lastRow)
            break
          }
        }

        const headers = [...data[0]]
        const values = data.filter(
          (data, index) => index !== 0 && data.every(value => value),
        )

        if (values.length < data.length - 1) {
          return setFileValidationError(
            'Wrong file data. Empty values are not acceptable.',
          )
        }

        if (headers[0] !== 'FLYERS_COUNT') {
          return setFileValidationError(
            'There is an error in FLYERS_COUNT header.',
          )
        }

        const flyersCountObj = values.reduce((acc, value) => {
          return {
            ...acc,
            [value[0]]: acc[value[0]] ? acc[value[0]] + 1 : 1,
          }
        }, {})

        const isDuplicatesFlyersCount = !Object.values(flyersCountObj).every(
          count => count === 1,
        )

        if (isDuplicatesFlyersCount) {
          return setFileValidationError(
            `There are duplicates flyers count in the file.`,
          )
        }

        const isInvalidFlyerFormatValues = values.some(flyerFormatValues => {
          const isInvalidValues = flyerFormatValues.some((value, index) => {
            const header = headers[index]

            if (index === 0 && !/^[1-9]+\d+$/.test(value)) {
              setFileValidationError(
                `Wrong FLYERS_COUNT value ${value}. Only an integer value is allowed.`,
              )
              return true
            }

            // Skip validation for TOTAL_DATA_COST as it's calculated
            if (header === 'TOTAL_DATA_COST') {
              return false
            }

            if (
              index !== 0 &&
              value !== '' &&
              (isNaN(value) || Number(value) < 0)
            ) {
              setFileValidationError(
                `Wrong value ${value}. Printing cost can only include numbers and one dot.`,
              )
              return true
            }

            return false
          })

          if (isInvalidValues) {
            return true
          } else {
            return false
          }
        })

        if (isInvalidFlyerFormatValues) return
      },
    })
  }

  const handleFileUpload = async files => {
    validateUploadedFile(files[0])
    setUploadedFile(files[0])
  }

  const handleTemplateDownload = () => {
    const headers = [
      'FLYERS_COUNT',
      ADMDocumentFormats.POSTCARD_6x4,
      ADMDocumentFormats.POSTCARD_9x6,
      ADMDocumentFormats.POSTCARD_11x6,
      ADMDocumentFormats.POSTCARD_15x12,
      'DATA_COST',
    ]
    const printingCostsTemplate =
      headers.join(',') +
      '\n' +
      headers.map(() => '').join(',') +
      '\n' +
      headers.map(() => '').join(',')
    downloadStringAsFile(
      printingCostsTemplate,
      'ADMClientsPricing.csv',
      'text/csv',
    )
  }

  const renderParentHeaders = () => {
    if (!headers.structure) return null

    return (
      <TableRow>
        <TableCell align="left" className={classes.borderRight} rowSpan={2}>
          <Typography variant="subtitle2" style={{ fontWeight: 'bold' }}>
            Quantity
          </Typography>
        </TableCell>
        <TableCell
          align="center"
          colSpan={4}
          className={classes.borderRight}
          style={{ borderBottom: '1px solid #e0e0e0' }}
        >
          <Typography variant="subtitle2" style={{ fontWeight: 'bold' }}>
            Default Pricing Table
          </Typography>
        </TableCell>
        <TableCell
          align="center"
          colSpan={2}
          className={classes.borderRight}
          style={{ borderBottom: '1px solid #e0e0e0' }}
        >
          <Typography variant="subtitle2" style={{ fontWeight: 'bold' }}>
            Aggregation Cost (Data Axle)
          </Typography>
        </TableCell>
      </TableRow>
    )
  }

  const renderSubHeaders = () => {
    if (!headers.structure) return null

    return (
      <TableRow>
        {headers.structure.defaultPricing.children.map(header => (
          <TableCell
            key={header}
            align="center"
            className={classes.borderRight}
          >
            <Typography variant="body2">{ADMDocumentLabels[header]}</Typography>
          </TableCell>
        ))}
        {headers.structure.aggregationCost.children.map(header => (
          <TableCell
            key={header}
            align="center"
            className={classes.borderRight}
          >
            <Typography variant="body2">
              {AggregationCostLabels[header]}
            </Typography>
          </TableCell>
        ))}
      </TableRow>
    )
  }

  const handleShowModal = () => {
    if (isEditingTable) {
      return
    }

    setShowModal(true)
  }

  return visible ? (
    <Box>
      <ExpandCollapseBox title={'ADM Clients pricing'}>
        <Box display={'flex'} justifyContent={'flex-end'}>
          {/* {printingCosts.length ? (
            <ButtonInline
              className={classes.uploadBtn}
              label={'Download costs'}
              icon={<DownloadIcon style={{ marginLeft: '7px' }} />}
              labelFirst
              onClickHandler={() => unparseFile(printingCosts)}
            />
          ) : null} */}
          {printingCosts.length ? (
            <ButtonInline
              className={classes.uploadBtn}
              label={'Upload new costs'}
              icon={<EditIcon style={{ marginLeft: '7px' }} />}
              labelFirst
              onClickHandler={handleShowModal}
            />
          ) : null}
        </Box>
        {printingCosts.length ? (
          <Box display="flex" flexDirection="column" alignItems="end">
            <TableContainer className={classes.container}>
              <Table stickyHeader aria-label="sticky table" size="small">
                <TableHead className={classes.tableHead}>
                  {renderParentHeaders()}
                  {renderSubHeaders()}
                </TableHead>
                <TableBody>
                  {printingCosts.map(row => {
                    return (
                      <TableRow hover tabIndex={-1} key={row['FLYERS_COUNT']}>
                        {headers.flat &&
                          headers.flat.map((column, index) => {
                            const value = row[column]
                            return (
                              <TableCell
                                key={column}
                                align={index === 0 ? 'left' : 'center'}
                                className={classes.borderRight}
                              >
                                <Typography>
                                  {column === 'FLYERS_COUNT'
                                    ? Number(value).toLocaleString()
                                    : value !== undefined && value !== null
                                    ? column === 'TOTAL_DATA_COST'
                                      ? `${currencySign} ${formatTotalDataCost(
                                          value,
                                        )}`
                                      : `${currencySign} ${Number(
                                          value,
                                        ).toFixed(4)}`
                                    : '-'}
                                </Typography>
                              </TableCell>
                            )
                          })}
                      </TableRow>
                    )
                  })}
                </TableBody>
              </Table>
            </TableContainer>
            <Box marginTop={1}>
              <Typography className={classes.dmCostsUpdatedData}>
                Table version
                {ADMPrintingCostsData[0] ? ADMPrintingCostsData[0].version : 0},
                Last updated{' '}
                {moment(printingCosts[0].updatedAt).format('MMM DD, YYYY')}
              </Typography>
              {lastUpdaterUser && (
                <Typography className={classes.dmCostsUpdatedData}>
                  by {lastUpdaterUser.firstName} {lastUpdaterUser.lastName}
                </Typography>
              )}
            </Box>
          </Box>
        ) : (
          <Box className={classes.noDataWrapper}>
            <Box className={classes.noData}>
              <Typography>No Data</Typography>
              <FileCopyTwoToneIcon color="#8A96A0" />
            </Box>
            <Link component="span" variant="body2" onClick={handleShowModal}>
              Upload new costs
            </Link>
          </Box>
        )}
      </ExpandCollapseBox>
      <Modal show={showModal} onEscapeKeyDown={() => setShowModal(false)}>
        <ModalHeader>
          <ModalTitle>Upload printing costs</ModalTitle>
        </ModalHeader>
        <ModalBody>
          <Box>
            <Typography className={classes.mb16}>
              Upload a file in format .csv with the required column formatting.
              You can find a template here:{' '}
              <span
                className={classes.downloadTemplate}
                onClick={() => handleTemplateDownload()}
              >
                download template
              </span>
            </Typography>
            {/* <Typography className={classes.mb16}>
              {isUSOrCA
                ? '‘A6#250’, ‘DOOR_HANGER#250‘, and ‘TEARAWAY#250‘'
                : '‘A6#250‘ and ‘TEARAWAY#250‘'}{' '}
              formats must be present in the file. You can add multiple flyer
              weights, just make sure that each is in its own column (ex:
              A6#250, A6#300) .
            </Typography> */}
            <Typography className={cn(classes.underlined, classes.mb16)}>
              Existing data will be overwritten when a new file is uploaded.
            </Typography>
            <Typography className={classes.mb16}>
              On uploading, costs will automatically be rounded to 4 decimals.
              For example, $0.055289 will be rounded to $0.0553
            </Typography>
          </Box>

          {uploadedFile ? (
            <Box className={classes.uploaded}>
              <Typography>{uploadedFile.name}</Typography>
              <CancelIcon
                onClick={() => {
                  setUploadedFile(null)
                  setFileValidationError('')
                }}
              />
            </Box>
          ) : (
            <Box
              className={classes.uploadWrapper}
              onDrop={e => {
                e.preventDefault()
                e.stopPropagation()

                if (!uploadedFile) {
                  handleFileUpload(e.dataTransfer.files)
                }
              }}
              onDragOver={e => {
                e.preventDefault()
                e.stopPropagation()
              }}
            >
              <input
                style={{ display: 'none' }}
                id="upload-dm-printing-costs"
                type="file"
                onChange={e =>
                  !uploadedFile ? handleFileUpload(e.target.files) : {}
                }
                accept=".csv, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/vnd.ms-excel"
              />
              <label
                htmlFor="upload-dm-printing-costs"
                style={{
                  width: '100%',
                  height: '100%',
                  cursor: 'pointer',
                }}
              >
                <Box className={classes.upload}>
                  <UploadIcon />
                  <Typography>Upload</Typography>
                </Box>
              </label>
            </Box>
          )}
          {fileValidationError && (
            <Typography className={classes.fileValidationError}>
              Error! {fileValidationError}
            </Typography>
          )}
        </ModalBody>
        <ModalFooter>
          <SubmitButton
            value="Confirm"
            disabled={!uploadedFile || fileValidationError}
            onClick={() => parseFile()}
          />
          <SubmitButton
            value="Cancel"
            onClick={() => {
              setShowModal(false)
              setUploadedFile(null)
              setFileValidationError('')
            }}
          />
        </ModalFooter>
      </Modal>
    </Box>
  ) : null
}
